import * as React from "react";
import { Box, TextField, Typography, useTheme } from "@mui/material";
import { ProductModel } from "../../../../../../models/Product.model";

// ------------------------------------------------------------------
// Concentration input — identical validation logic, only sx changes
// ------------------------------------------------------------------
type ConcentrationInputProps = {
    product: ProductModel;
    onChange: (id: number, value: string) => void;
};

const ConcentrationInput: React.FC<ConcentrationInputProps> = ({ product, onChange }) => {
    const theme = useTheme();
    const [inputValue, setInputValue] = React.useState<string>(product.percentage ? product.percentage + "%" : "");
    const [warning, setWarning] = React.useState<string>(() => {
        const pct = Number(product.percentage);
        if (!product.percentage) return "";
        return pct < Number(product.minimumDosage) || pct > Number(product.maxDosage) ? "Fora do intervalo" : "";
    });
    const inputRef = React.useRef("");

    const validateLimits = (value: string) => {
        const num = +value.replace(/%/g, "");
        const ceiling = num > 100 ? 100 : num;
        if (ceiling < +product.minimumDosage || ceiling > +product.maxDosage) {
            setWarning("Fora do intervalo");
        } else {
            setWarning("");
        }
        return ceiling;
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const newString = event.target.value
            .replace(/[^\d,.]/g, "")
            .replace(",", ".")
            .replace(/\.(?=.*\.)+/g, "")
            .slice(0, 5);
        validateLimits(newString);
        setInputValue(newString);
        const num = +newString.replace(/%/g, "");
        const ceiling = num > 100 ? 100 : num;
        onChange(product.idProduct || 0, ceiling.toString());
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === "") {
            setInputValue("");
            return;
        }
        const ceiling = validateLimits(e.target.value);
        setInputValue(String(ceiling) + "%");
    };

    if (!product.showConcentration) return null;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 96 }}>
            <TextField
                color={"primary"}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                size="small"
                placeholder="0%"
                sx={{
                    width: 96,
                    "& .MuiOutlinedInput-root": {
                        height: 46,
                        backgroundColor: theme.palette.brand.surface2,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                        borderWidth: "1.5px",
                    },
                    "& .MuiOutlinedInput-input": {
                        textAlign: "center",
                        height: 46,
                        padding: "0 8px",
                        color: "primary.main",
                        fontWeight: 600,
                        // 16px no mobile: abaixo disso o Safari do iPhone dá zoom
                        // automático ao focar o campo e não desfaz, o que empurra
                        // o rodapé fixo para fora da tela e deixa a página "solta".
                        fontSize: { xs: "16px", md: "15px" },
                        "&::placeholder": { color: "text.disabled", opacity: 1, fontWeight: 500 },
                    },
                }}
                InputProps={{
                    onFocus: (e) => setInputValue(e.target.value.replace(/%/g, "")),
                    inputRef: inputRef,
                }}
            />
            {warning && (
                <Typography sx={{ fontSize: "10px", color: "error.main", mt: 0.25, textAlign: "center" }}>{warning}</Typography>
            )}
        </Box>
    );
};

export default ConcentrationInput;
